import { Language } from '../hooks/useLanguage'

interface FooterIntroData {
  topRatedTitle: string
  topRatedSubtitle: string
  heading: string
  url: string
}

interface FooterMenuItem {
  columnName: string
  columnItems: { title: string; url: string }[]
}

interface FooterOutroData {
  copyRight: string
  outroItems: { title: string; url: string }[]
  languages: { language: string; url: string }[]
}

export const getFooterIntroData = (language: Language): FooterIntroData => {
  switch (language) {
    case 'en':
      return enFooterIntroData
    case 'fr':
      return frFooterIntroData
    case 'de':
      return deFooterIntroData
  }
}

export const getFooterMenuItems = (language: Language): FooterMenuItem[] => {
  switch (language) {
    case 'en':
      return enFooterMenuItems
    case 'fr':
      return frFooterMenuItems
    case 'de':
      return deFooterMenuItems
  }
}

export const getFooterOutroData = (language: Language): FooterOutroData => {
  switch (language) {
    case 'en':
      return enFooterOutroData
    case 'fr':
      return frFooterOutroData
    case 'de':
      return deFooterOutroData
  }
}

const enFooterIntroData: FooterIntroData = {
  topRatedTitle: 'Top rated',
  topRatedSubtitle: 'on major platforms',
  heading: 'airfocus is where teams build great products. Welcome home',
  url: '/',
}

const frFooterIntroData: FooterIntroData = {
  topRatedTitle: 'Excellentes notes',
  topRatedSubtitle: "sur la des plateformes d'évaluation",
  heading: 'airfocus est la platforme où les équipes construisent leurs produits. <br /> Bienvenue chez vous',
  url: '/fr/',
}

const deFooterIntroData: FooterIntroData = {
  topRatedTitle: 'Beste Bewertungen',
  topRatedSubtitle: 'auf den einschlägigen Plattformen',
  heading:
    'airfocus ist der Ort, an dem Teams großartige Produkte entwickeln. <br class="hide-on-small" /> Willkommen zu Hause',
  url: '/de/',
}

const enFooterMenuItems: FooterMenuItem[] = [
  {
    columnName: 'Company',
    columnItems: [
      {
        title: 'About us',
        url: '/about-us/',
      },
      {
        title: 'Contact sales',
        url: '/contact-sales/',
      },
      {
        title: 'Careers',
        url: '/careers/',
      },
      {
        title: "airfocus's portal",
        url: 'https://airfocusportal.airfocus.site/',
      },
      {
        title: 'Get in touch',
        url: '/get-in-touch/',
      },
      {
        title: 'Press',
        url: '/press/',
      },
    ],
  },
  {
    columnName: 'Product',
    columnItems: [
      {
        title: 'Overview',
        url: '/product/',
      },
      {
        title: 'Pricing',
        url: '/pricing/',
      },
      {
        title: 'Integrations',
        url: '/integrations/',
      },
      {
        title: 'API docs',
        url: 'https://developer.airfocus.com/',
      },
      {
        title: 'Security',
        url: '/security/',
      },
      {
        title: 'Case studies',
        url: '/resources/case-studies/',
      },
      {
        title: 'Compare',
        url: '/compare/',
      },
      {
        title: 'Status',
        url: 'https://status.airfocus.com',
      },
    ],
  },
  {
    columnName: 'Resources',
    columnItems: [
      {
        title: 'Academy',
        url: '/academy/',
      },
      {
        title: 'Help center',
        url: 'https://help.airfocus.com/',
      },
      // {
      //   title: 'Product walkthroughs',
      //   url: '/resources/events/learn-airfocus/',
      // },
      {
        title: 'Blog',
        url: '/blog/',
      },

      {
        title: 'Events',
        url: '/resources/events/',
      },
      {
        title: 'Templates',
        url: '/templates/',
      },
      {
        title: 'Product Heroes',
        url: '/product-heroes/',
      },
      {
        title: 'Glossary',
        url: '/glossary/',
      },
    ],
  },
  {
    columnName: 'Ultimate guides',
    columnItems: [
      {
        title: 'Product OKRs',
        url: '/resources/guides/objectives-okrs/',
      },
      {
        title: 'Product Strategy',
        url: '/resources/guides/product-strategy/',
      },
      {
        title: 'Prioritization',
        url: '/resources/guides/prioritization/',
      },
      {
        title: 'Internal Product Management',
        url: '/resources/guides/internal-pm/',
      },
      {
        title: 'Product Management',
        url: '/resources/guides/product-management/',
      },
      {
        title: 'Roadmaps',
        url: '/resources/guides/roadmaps/',
      },
      {
        title: "A Product Manager's Job",
        url: '/resources/guides/product-manager-job/',
      },
      {
        title: 'Product Operations',
        url: '/resources/guides/product-operations/',
      },
      {
        title: 'Customer Feedback',
        url: '/resources/guides/customer-feedback/',
      },
    ],
  },
]

const frFooterMenuItems: FooterMenuItem[] = [
  {
    columnName: 'Entreprise (ang.)',
    columnItems: [
      // {
      //   title: 'À propos',
      //   url: '/fr/about-us/',
      // },
      {
        title: "Contacter l'équipe commerciale",
        url: '/contact-sales/',
      },
      // {
      //   title: 'Carrières',
      //   url: '/fr/careers/',
      // },
      {
        title: 'Notre Roadmap',
        url: 'https://airfocusportal.airfocus.site/',
      },
      // {
      //   title: 'Nous contacter',
      //   url: '/fr/get-in-touch/',
      // },
      {
        title: 'Presse',
        url: '/press/',
      },
    ],
  },
  {
    columnName: 'Produit',
    columnItems: [
      {
        title: 'Découvrir notre produit',
        url: '/fr/product/',
      },
      {
        title: 'Objectifs & OKRs',
        url: '/fr/product/objectives-okrs/',
      },
      {
        title: 'Roadmap',
        url: '/fr/product/roadmaps/',
      },
      {
        title: 'Priorisation',
        url: '/fr/product/prioritization/',
      },
      {
        title: 'Gestion des retours utilisateurs',
        url: '/fr/product/insights/',
      },
      {
        title: 'Portail',
        url: '/fr/product/portal/',
      },
      {
        title: 'Tarifs',
        url: '/fr/pricing/',
      },
      // {
      //   title: 'Integrations',
      //   url: '/integrations/',
      // },
      // {
      //   title: 'Security',
      //   url: '/security/',
      // },
      // {
      //   title: 'Case studies',
      //   url: '/case-studies/',
      // },
    ],
  },
  {
    columnName: 'Resources (ang.)',
    columnItems: [
      // {
      //   title: 'Academy',
      //   url: '/academy/',
      // },
      {
        title: 'Help center',
        url: 'https://help.airfocus.com/',
      },
      {
        title: 'Status',
        url: 'https://status.airfocus.com',
      },
      {
        title: 'API documentation',
        url: 'https://developer.airfocus.com/',
      },
      // {
      //   title: 'Product walkthroughs',
      //   url: '/resources/events/learn-airfocus/',
      // },
      // {
      //   title: 'Blog',
      //   url: '/blog/',
      // },
      // {
      //   title: 'Events',
      //   url: '/resources/events/',
      // },
      // {
      //   title: 'Modèles',
      //   url: '/fr//templates/',
      // },
      // {
      //   title: 'Product Heroes',
      //   url: '/product-heroes/',
      // },
      // {
      //   title: 'Glossary',
      //   url: '/glossary/',
      // },
    ],
  },
  {
    columnName: 'Guides profonds (ang.)',
    columnItems: [
      {
        title: 'Product OKRs',
        url: '/resources/guides/objectives-okrs/',
      },
      {
        title: 'Product Strategy',
        url: '/resources/guides/product-strategy/',
      },
      {
        title: 'Prioritization',
        url: '/resources/guides/prioritization/',
      },
      {
        title: 'Internal Product Management',
        url: '/resources/guides/internal-pm/',
      },
      {
        title: 'Product Management',
        url: '/resources/guides/product-management/',
      },
      {
        title: 'Roadmaps',
        url: '/resources/guides/roadmaps/',
      },
      {
        title: "A Product Manager's Job",
        url: '/resources/guides/product-manager-job/',
      },
      {
        title: 'Product Operations',
        url: '/resources/guides/product-operations/',
      },
      {
        title: 'Customer Feedback',
        url: '/resources/guides/customer-feedback/',
      },
    ],
  },
]

const deFooterMenuItems: FooterMenuItem[] = [
  {
    columnName: 'Unternehmen (en)',
    columnItems: [
      // {
      //   title: 'Über uns',
      //   url: '/about-us/',
      // },
      {
        title: 'Vertrieb kontaktieren',
        url: '/contact-sales/',
      },
      // {
      //   title: 'Karriere',
      //   url: '/careers/',
      // },
      {
        title: "airfocus' Portal",
        url: 'https://airfocusportal.airfocus.site/',
      },
      // {
      //   title: 'Kontakt',
      //   url: '/get-in-touch/',
      // },
      {
        title: 'Presse',
        url: '/press/',
      },
    ],
  },
  {
    columnName: 'Produkt',
    columnItems: [
      {
        title: 'Überblick',
        url: '/de/product/',
      },
      {
        title: 'Objectives & OKRs',
        url: '/de/product/objectives-okrs/',
      },
      {
        title: 'Roadmaps',
        url: '/de/product/roadmaps/',
      },
      {
        title: 'Priorisierung',
        url: '/de/product/prioritization/',
      },
      {
        title: 'Feedback & Erkenntnisse',
        url: '/de/product/insights/',
      },
      {
        title: 'Portal',
        url: '/de/product/portal/',
      },
      {
        title: 'Preise',
        url: '/de/pricing/',
      },
      // {
      //   title: 'Integrationen',
      //   url: '/integrations/',
      // },
      // {
      //   title: 'Sicherheit',
      //   url: '/security/',
      // },
      // {
      //   title: 'Case Studies',
      //   url: '/case-studies/',
      // },
    ],
  },
  {
    columnName: 'Resourcen (en)',
    columnItems: [
      // {
      //   title: 'Academy',
      //   url: '/academy/',
      // },
      {
        title: 'Hilfecenter',
        url: 'https://help.airfocus.com/',
      },
      {
        title: 'Status',
        url: 'https://status.airfocus.com',
      },
      {
        title: 'API Dokumentation',
        url: 'https://developer.airfocus.com/',
      },
      // {
      //   title: 'Product Walkthroughs',
      //   url: '/resources/events/learn-airfocus/',
      // },
      // {
      //   title: 'Blog',
      //   url: '/blog/',
      // },

      // {
      //   title: 'Events',
      //   url: '/resources/events/',
      // },
      // {
      //   title: 'Templates',
      //   url: '/templates/',
      // },
      // {
      //   title: 'Product Heroes',
      //   url: '/product-heroes/',
      // },
      // {
      //   title: 'Glossar',
      //   url: '/glossary/',
      // },
    ],
  },
  {
    columnName: 'Fundierte Ratgeber (en)',
    columnItems: [
      {
        title: 'Product OKRs',
        url: '/resources/guides/objectives-okrs/',
      },
      {
        title: 'Product Strategy',
        url: '/resources/guides/product-strategy/',
      },
      {
        title: 'Prioritization',
        url: '/resources/guides/prioritization/',
      },
      {
        title: 'Internal Product Management',
        url: '/resources/guides/internal-pm/',
      },
      {
        title: 'Product Management',
        url: '/resources/guides/product-management/',
      },
      {
        title: 'Roadmaps',
        url: '/resources/guides/roadmaps/',
      },
      {
        title: "A Product Manager's Job",
        url: '/resources/guides/product-manager-job/',
      },
      {
        title: 'Product Operations',
        url: '/resources/guides/product-operations/',
      },
      {
        title: 'Customer Feedback',
        url: '/resources/guides/customer-feedback/',
      },
    ],
  },
]

const enFooterOutroData: FooterOutroData = {
  copyRight: 'All rights reserved.',
  outroItems: [
    { title: 'Terms of service', url: '/terms/' },
    { title: 'Privacy', url: '/privacy/' },
    { title: 'Careers privacy policy', url: '/careers-privacy/' },
    { title: 'Legal notice', url: '/legal/' },
  ],
  languages: [
    {
      language: 'DE',
      url: 'https://airfocus.com/de/',
    },
    {
      language: 'FR',
      url: 'https://airfocus.com/fr/',
    },
  ],
}

const frFooterOutroData: FooterOutroData = {
  copyRight: 'All rights reserved.',
  outroItems: [
    { title: 'Terms of service', url: '/terms/' },
    { title: 'Privacy', url: '/privacy/' },
    { title: 'Careers privacy policy', url: '/careers-privacy/' },
    { title: 'Legal notice', url: '/legal/' },
  ],
  languages: [
    {
      language: 'EN',
      url: 'https://airfocus.com/',
    },
    {
      language: 'DE',
      url: 'https://airfocus.com/de/',
    },
  ],
}

const deFooterOutroData: FooterOutroData = {
  copyRight: 'Alle Rechte vorbehalten.',
  outroItems: [
    { title: 'AGB', url: '/terms/' },
    { title: 'Datenschutzvereinbarung', url: '/privacy/' },
    { title: 'Datenschutzvereinbarung für Bewerber', url: '/careers-privacy/' },
    { title: 'Impressum', url: '/legal/' },
  ],
  languages: [
    {
      language: 'EN',
      url: 'https://airfocus.com/',
    },
    {
      language: 'FR',
      url: 'https://airfocus.com/fr/',
    },
  ],
}
